import { cn } from '@/lib/utils'

export interface PasswordValidation {
  minLength: boolean
  upper: boolean
  lower: boolean
  digit: boolean
  special: boolean
}

interface PasswordStrengthProps {
  password: string
}

const SPECIAL_RE = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/

// CHECKPOINT: PRD-01 FR-1.1.3 — Enforce 8-character minimum password length.
export function validatePassword(password: string): PasswordValidation {
  return {
    minLength: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: SPECIAL_RE.test(password),
  }
}

export function getStrengthScore(password: string): number {
  const v = validatePassword(password)
  return Object.values(v).filter(Boolean).length
}

export function PasswordStrength({ password }: PasswordStrengthProps) {
  const score = getStrengthScore(password)
  const checks = [
    { key: 'minLength', label: 'At least 8 characters' },
    { key: 'upper', label: 'One uppercase letter' },
    { key: 'lower', label: 'One lowercase letter' },
    { key: 'digit', label: 'One number' },
    { key: 'special', label: 'One special character' },
  ] as const

  const validation = validatePassword(password)

  const strengthLabels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent']
  const strengthColor = [
    'bg-destructive',
    'bg-destructive',
    'bg-amber-500',
    'bg-amber-500',
    'bg-emerald-500',
    'bg-emerald-500',
  ][score]

  if (!password) return null

  return (
    <div className="space-y-2 animate-fade-in">
      <div className="flex items-center gap-2">
        <div className="flex h-1.5 flex-1 gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-full flex-1 rounded-full transition-colors duration-300',
                i < score ? strengthColor : 'bg-muted'
              )}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground w-20 text-right">
          {strengthLabels[score]}
        </span>
      </div>
      <ul className="grid grid-cols-1 gap-1 text-xs text-muted-foreground">
        {checks.map(({ key, label }) => {
          const passed = validation[key]
          return (
            <li key={key} className={cn('flex items-center gap-1.5 transition-colors', passed && 'text-emerald-500')}>
              <span
                className={cn(
                  'inline-block h-1.5 w-1.5 rounded-full',
                  passed ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                )}
              />
              {label}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
